import { useEffect, useRef, useState } from 'react';
import { Info } from 'lucide-react';
import { Button, Description, Modal, Spinner, type OverlayTriggerState } from '@heroui/react';
import { AsyncButton } from '../../../components/ui/async-button';
import { OtpInput } from '../../../components/ui/otp-input';
import {
  clearPhoneRecaptcha,
  confirmPhoneVerificationCode,
  sendPhoneVerificationCode,
} from '../../../lib/firebase-phone';
import { showErrorFromUnknown } from '../../../lib/toast';
import { isPhoneVerificationSkipped, maskPhone } from '../../../utils/phone';

interface PhoneVerificationModalProps {
  state: OverlayTriggerState;
  phone: string;
  onVerified: () => void | Promise<void>;
}

export function PhoneVerificationModal({ state, phone, onVerified }: PhoneVerificationModalProps) {
  const [verificationId, setVerificationId] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [codeSent, setCodeSent] = useState(false);
  const sendStartedForOpenRef = useRef(false);

  const resetState = () => {
    sendStartedForOpenRef.current = false;
    setVerificationId(null);
    setCode('');
    setSending(false);
    setVerifying(false);
    setError(null);
    setCodeSent(false);
    clearPhoneRecaptcha();
  };

  useEffect(() => {
    if (!state.isOpen) {
      resetState();
    }
  }, [state.isOpen]);

  const handleSendCode = async (force = false) => {
    if (isPhoneVerificationSkipped()) {
      setVerificationId('dev-skip');
      setCodeSent(true);
      setCode('');
      return;
    }

    if (force) {
      sendStartedForOpenRef.current = true;
    }

    setSending(true);
    setError(null);
    try {
      const id = await sendPhoneVerificationCode(phone);
      setVerificationId(id);
      setCodeSent(true);
      setCode('');
    } catch (err) {
      showErrorFromUnknown(err, 'Não foi possível enviar o código.');
      setError(err instanceof Error ? err.message : 'Não foi possível enviar o código.');
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    if (!state.isOpen || !phone.trim() || sendStartedForOpenRef.current) return;
    sendStartedForOpenRef.current = true;
    void handleSendCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- dispara uma vez ao abrir
  }, [state.isOpen, phone]);

  const handleVerify = async (value = code) => {
    if (isPhoneVerificationSkipped()) {
      setVerifying(true);
      setError(null);
      try {
        await onVerified();
        state.close();
      } catch (err) {
        showErrorFromUnknown(err, 'Não foi possível salvar.');
        setError(err instanceof Error ? err.message : 'Não foi possível salvar.');
      } finally {
        setVerifying(false);
      }
      return;
    }

    if (!verificationId || value.length < 6) return;

    setVerifying(true);
    setError(null);
    try {
      await confirmPhoneVerificationCode(phone, verificationId, value);
      await onVerified();
      state.close();
    } catch (err) {
      showErrorFromUnknown(err, 'Código inválido ou expirado.');
      setError(err instanceof Error ? err.message : 'Código inválido ou expirado.');
    } finally {
      setVerifying(false);
    }
  };

  return (
    <Modal state={state}>
      <Modal.Backdrop isDismissable className="z-[200]">
        <Modal.Container placement="center" className="z-[200]">
          <Modal.Dialog>
            <Modal.Header>
              <Modal.Heading>Verificar telefone</Modal.Heading>
              <Modal.CloseTrigger />
            </Modal.Header>
            <Modal.Body className="flex flex-col gap-4">
              <Description>
                {isPhoneVerificationSkipped() ? (
                  <>Confirme o telefone <strong>{maskPhone(phone)}</strong> para continuar.</>
                ) : (
                  <>
                    Enviamos um código SMS para <strong>{maskPhone(phone)}</strong>. Digite os 6
                    dígitos abaixo.
                  </>
                )}
              </Description>

              {typeof window !== 'undefined' && window.location.hostname === 'localhost' ? (
                <p className="flex items-start gap-1 text-sm text-warning">
                  <Info size={14} className="mt-0.5 shrink-0" />
                  SMS não funciona em localhost. Abra http://127.0.0.1:5173 e adicione 127.0.0.1 nos
                  domínios autorizados do Firebase.
                </p>
              ) : null}

              {isPhoneVerificationSkipped() ? (
                <p className="flex items-start gap-1 text-sm text-warning">
                  <Info size={14} className="mt-0.5 shrink-0" />
                  Verificação por SMS desativada neste ambiente. Clique em confirmar para continuar.
                </p>
              ) : null}

              {!isPhoneVerificationSkipped() ? (
                <div className="flex justify-center py-2">
                  <OtpInput
                    value={code}
                    disabled={!codeSent || verifying}
                    onChange={setCode}
                    onComplete={(value) => void handleVerify(value)}
                  />
                </div>
              ) : null}

              {error ? <p className="text-center text-sm text-danger">{error}</p> : null}

              {sending ? (
                <div className="flex justify-center">
                  <Spinner size="sm" />
                </div>
              ) : null}
            </Modal.Body>
            <Modal.Footer className="flex flex-col gap-2 sm:flex-row sm:justify-end">
              {!isPhoneVerificationSkipped() ? (
                <AsyncButton
                  variant="secondary"
                  isPending={sending}
                  isDisabled={verifying}
                  onPress={() => void handleSendCode(true)}
                >
                  Reenviar código
                </AsyncButton>
              ) : null}
              <AsyncButton
                variant="primary"
                isPending={verifying}
                isDisabled={
                  isPhoneVerificationSkipped()
                    ? false
                    : !codeSent || code.length < 6
                }
                pendingLabel="Confirmar"
                onPress={() => void handleVerify()}
              >
                Confirmar
              </AsyncButton>
            </Modal.Footer>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  );
}
